/**
 * Utility function to download prescription PDF from backend
 */

import { getToken, intl } from 'dmsbasereact';
import { getBaseURL } from "../build_config";

/**
 * Opens a prescription PDF in print mode
 * @param {number} prescriptionPk - The primary key of the prescription
 * @returns {Promise<boolean>} - Returns true if successful
 */
export const openPrescriptionPdfForPrint = async (prescriptionPk) => {
  try {
    const token = getToken();
    const url = `${getBaseURL()}/prescription/export_pdf/${prescriptionPk}`;

    // Try to open window first to avoid popup blockers
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      console.error('Failed to open window - popup blocker may be active');
      return false;
    }

    // Show loading message in the window with translations
    const loadingTitle = intl.get("LoadingPdf").d("Loading PDF...");
    const loadingMessage = intl.get("LoadingPdfMessage").d("Please wait while we prepare your document.");
    const loadingTime = intl.get("LoadingPdfTime").d("This may take up to 30 seconds.");

    printWindow.document.write(`
      <html>
        <head>
          <title>${loadingTitle}</title>
          <style>
            body {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
              background: #f5f5f5;
            }
            .container {
              text-align: center;
              padding: 40px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            .spinner {
              width: 50px;
              height: 50px;
              border: 4px solid #e0e0e0;
              border-top: 4px solid #1976d2;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            h2 { color: #333; margin: 0 0 10px; }
            p { color: #666; margin: 5px 0; }
            .time { font-size: 14px; color: #888; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="spinner"></div>
            <h2>${loadingTitle}</h2>
            <p>${loadingMessage}</p>
            <p class="time">${loadingTime}</p>
          </div>
        </body>
      </html>
    `);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
        'Accept': 'application/pdf'
      }
    });

    if (!response.ok) {
      printWindow.close();
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = intl.get("Prescription").d("Prescription");
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace('.pdf', '');
      }
    }

    // Embed PDF in iframe to keep window title
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${filename}</title>
          <style>
            body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
            iframe { width: 100%; height: 100%; border: none; }
          </style>
        </head>
        <body>
          <iframe src="${blobUrl}" id="pdfFrame"></iframe>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Clean up after 60 seconds
    setTimeout(() => {
      try {
        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error('Error revoking object URL:', e);
      }
    }, 60000);

    return true;
  } catch (error) {
    console.error('Error opening prescription PDF for print:', error);
    return false;
  }
};

/**
 * Downloads a prescription PDF file
 * @param {number} prescriptionPk - The primary key of the prescription
 * @returns {Promise<boolean>} - Returns true if download was successful
 */
export const downloadPrescriptionPdf = async (prescriptionPk) => {
  try {
    const token = getToken();
    const url = `${getBaseURL()}/prescription/export_pdf/${prescriptionPk}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
        'Accept': 'application/pdf'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the blob from response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'prescription.pdf';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    return true;
  } catch (error) {
    console.error('Error downloading prescription PDF:', error);
    return false;
  }
};
